/**
 * Sprint 5 · Form Builder · 依 field_type 動態 render 輸入元件（填寫頁用）。
 * 平台無此 precedent，本元件為 schema-driven form 的基礎。
 */
import React from 'react';
import {
  Checkbox, FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup,
  Stack, TextField, Typography,
} from '@mui/material';

import { FieldSpec } from '../services/formBuilderService';

interface Props {
  field: FieldSpec;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  readOnly?: boolean;
}

const labelOf = (f: FieldSpec) => (f.label && (f.label['zh-TW'] || Object.values(f.label)[0])) || f.field_name;

const FieldRenderer: React.FC<Props> = ({ field: f, value, onChange, readOnly }) => {
  if (f.field_type === 'hidden') return null;
  const label = labelOf(f);
  const opts = f.options || [];
  const set = (v: unknown) => onChange(f.field_name, v);
  const common = {
    fullWidth: true, size: 'small' as const, label, required: f.required,
    disabled: readOnly, InputProps: readOnly ? { readOnly: true } : undefined,
  };

  switch (f.field_type) {
    case 'textarea':
      return <TextField {...common} multiline minRows={3} value={String(value ?? '')}
        onChange={(e) => set(e.target.value)} inputProps={{ maxLength: f.length || undefined }} />;
    case 'number':
      return <TextField {...common} type="number" value={value ?? ''}
        onChange={(e) => set(e.target.value === '' ? '' : Number(e.target.value))} />;
    case 'date':
      return <TextField {...common} type="date" InputLabelProps={{ shrink: true }}
        value={String(value ?? '')} onChange={(e) => set(e.target.value)} />;
    case 'datetime':
      return <TextField {...common} type="datetime-local" InputLabelProps={{ shrink: true }}
        value={String(value ?? '')} onChange={(e) => set(e.target.value)} />;
    case 'file':
      return <TextField {...common} type="file" InputLabelProps={{ shrink: true }}
        onChange={(e) => set((e.target as HTMLInputElement).files?.[0]?.name || '')} />;
    case 'select':
      return (
        <TextField {...common} select value={String(value ?? '')} onChange={(e) => set(e.target.value)}>
          <MenuItem value=""><em>—</em></MenuItem>
          {opts.map((o, i) => (
            <MenuItem key={i} value={String(o.value)}>{o.label}</MenuItem>
          ))}
        </TextField>
      );
    case 'radio':
      return (
        <FormControl disabled={readOnly} required={f.required}>
          <FormLabel>{label}</FormLabel>
          <RadioGroup row value={String(value ?? '')} onChange={(e) => set(e.target.value)}>
            {opts.map((o, i) => (
              <FormControlLabel key={i} value={String(o.value)} control={<Radio size="small" />} label={o.label} />
            ))}
          </RadioGroup>
        </FormControl>
      );
    case 'checkbox': {
      const arr: string[] = Array.isArray(value) ? (value as string[]).map(String) : [];
      const toggle = (v: string) => set(arr.includes(v) ? arr.filter((x) => x !== v) : arr.concat(v));
      return (
        <FormControl disabled={readOnly} required={f.required}>
          <FormLabel>{label}</FormLabel>
          <Stack direction="row" flexWrap="wrap">
            {opts.map((o, i) => (
              <FormControlLabel key={i}
                control={<Checkbox size="small" checked={arr.includes(String(o.value))}
                  onChange={() => toggle(String(o.value))} />} label={o.label} />
            ))}
          </Stack>
        </FormControl>
      );
    }
    case 'text':
    default:
      return <TextField {...common} value={String(value ?? '')}
        onChange={(e) => set(e.target.value)} inputProps={{ maxLength: f.length || undefined }} />;
  }
};

export const fieldLabel = labelOf;
export const FieldReadonlyValue: React.FC<{ field: FieldSpec; value: unknown }> = ({ field, value }) => {
  let v = value;
  if (Array.isArray(v)) v = v.join('、');
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.5, borderBottom: '1px dashed #eee' }}>
      <Typography variant="body2" sx={{ minWidth: 120, color: 'text.secondary' }}>{labelOf(field)}</Typography>
      <Typography variant="body2">{String(v ?? '')}</Typography>
    </Stack>
  );
};

export default FieldRenderer;
